import type { i18n as I18nInstance } from 'i18next';

/**
 * Explicit language choice made with the header toggle. Lives in
 * `localStorage` (not the session store) so the choice survives a new tab and
 * a reload; it holds a two-letter locale code and nothing about the member.
 */
export const LOCALE_PREFERENCE_KEY = 'galapagos.web.locale';

// Every storage touch is guarded twice: the global is absent under `next build`
// (Node) and the accessor itself can throw in privacy modes. Either degrades to
// "no preference", never to a crash (mirrors `services/session.ts`).
export function readLocalePreference(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(LOCALE_PREFERENCE_KEY);
  } catch {
    return null;
  }
}

export function writeLocalePreference(locale: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LOCALE_PREFERENCE_KEY, locale);
  } catch {
    // The switch still applies in memory for this session.
  }
}

/** URL parameter a link uses to name the language it should open in. */
export const LOCALE_QUERY_PARAM = 'lng';

/**
 * The language a link asks for with `?lng=`, as a base code. Marketing and
 * support links use whatever code their author knows (`es`, `es-MX`, `es-US`),
 * so only the part before the region is kept; whether it is bundled is
 * decided by `resolveInitialLocale`.
 */
export function readRequestedLocale(search: string): string | null {
  try {
    const raw = new URLSearchParams(search).get(LOCALE_QUERY_PARAM);
    const base = raw?.trim().split('-')[0]?.toLowerCase();
    return base ? base : null;
  } catch {
    return null;
  }
}

export interface ResolveInitialLocaleInput {
  /** Language named by the URL's `?lng=`, or null when absent. */
  requested?: string | null;
  /** Stored explicit preference, or null when none / unreadable. */
  stored: string | null;
  /** `navigator.language` when a browser is present; undefined at build time. */
  navigatorLanguage: string | undefined;
  /** Locales the build actually bundles (`Object.keys(snapshotResources)`). */
  available: readonly string[];
  /** Default locale when nothing else applies. */
  fallback: string;
}

/**
 * Pure precedence: `?lng=` → stored preference → browser language prefix →
 * fallback. A link always lands in the language it names, and the toggle's
 * remembered choice outranks the browser. A candidate that is not bundled is
 * ignored so a stale preference, a mistyped link or an unsupported browser
 * language never selects a locale with no strings.
 */
export function resolveInitialLocale({
  requested,
  stored,
  navigatorLanguage,
  available,
  fallback,
}: ResolveInitialLocaleInput): string {
  const isBundled = (candidate: string | null | undefined): candidate is string =>
    typeof candidate === 'string' && available.includes(candidate);

  if (isBundled(requested)) return requested;
  if (isBundled(stored)) return stored;

  const prefix = navigatorLanguage?.split('-')[0]?.toLowerCase();
  if (isBundled(prefix)) return prefix;

  return fallback;
}

/**
 * Every language switch goes through here: persist the choice, switch the
 * active language, then write it into the URL's `?lng=` so a reload keeps it
 * rather than a stale `?lng=` from the arrival link winning.
 */
export async function selectLocale(i18n: I18nInstance, locale: string): Promise<void> {
  writeLocalePreference(locale);
  await i18n.changeLanguage(locale);
  syncLocaleToUrl(locale);
}

/**
 * Write the chosen language into the address bar's `?lng=`, so a reload or a
 * shared link keeps the language on screen; otherwise a stale `?lng=` from the
 * arrival link would outrank the choice on the next load. `replaceState`
 * keeps every other parameter and the hash, and adds no Back step. The state
 * argument is null on purpose: Next.js treats a state carrying its own
 * history markers as its internal call and skips updating `useSearchParams`,
 * so the next step would carry the old `lng` forward. Given null, Next copies
 * its markers across itself and updates the router.
 */
export function syncLocaleToUrl(locale: string): void {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(LOCALE_QUERY_PARAM) === locale) return;
    url.searchParams.set(LOCALE_QUERY_PARAM, locale);
    window.history.replaceState(null, '', url.toString());
  } catch {
    // A blocked history API leaves the URL as it was; the switch still applies.
  }
}
